import React, { useState } from "react";

import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import {
  TrashIcon,
  DotsIcon,
  checkIcon,
} from "@excalidraw/excalidraw/components/icons";

import "./FileCard.scss";

import type {
  CustomFileMetadata,
  FolderMetadata,
} from "../../types/customFileLibrary";

interface FileCardProps {
  file: CustomFileMetadata;
  folders?: FolderMetadata[];
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRename?: (id: string, newName: string) => void;
  onMoveToFolder?: (fileId: string, folderId: string | null) => void;
}

/**
 * Format date as relative time (e.g., "2h ago", "3d ago")
 */
function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return "just now";
  } else if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 30) {
    return `${diffDays}d ago`;
  }
  return date.toLocaleDateString();
}

export const FileCard: React.FC<FileCardProps> = ({
  file,
  folders = [],
  onSelect,
  onDelete,
  onRename,
  onMoveToFolder,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showMainMenu, setShowMainMenu] = useState(false);
  const [showActionsSubmenu, setShowActionsSubmenu] = useState(false);
  const [showMoveSubmenu, setShowMoveSubmenu] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [editName, setEditName] = useState(file.name);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showConfirm) {
      onDelete(file.id);
      setShowConfirm(false);
    } else {
      setShowConfirm(true);
      // Auto-hide confirmation after 3 seconds
      setTimeout(() => setShowConfirm(false), 3000);
    }
  };

  const handleMoveToFolder = (folderId: string | null) => {
    if (onMoveToFolder) {
      onMoveToFolder(file.id, folderId);
    }
    setShowMainMenu(false);
    setShowMoveSubmenu(false);
  };

  const handleRenameClick = () => {
    setIsRenaming(true);
    setEditName(file.name);
    setShowMainMenu(false);
    setShowActionsSubmenu(false);
  };

  const handleRename = async () => {
    const trimmedName = editName.trim();
    if (trimmedName && trimmedName !== file.name && onRename) {
      try {
        await onRename(file.id, trimmedName);
        setIsRenaming(false);
      } catch (error) {
        // On error, reset to original name
        setEditName(file.name);
        setIsRenaming(false);
      }
    } else {
      setEditName(file.name);
      setIsRenaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleRename();
    } else if (e.key === "Escape") {
      setEditName(file.name);
      setIsRenaming(false);
    }
  };

  return (
    <div className="FileCard" onDoubleClick={() => onSelect(file.id)}>
      <div className="FileCard__thumbnail">
        {file.thumbnail ? (
          <img src={file.thumbnail} alt={file.name} />
        ) : (
          <div className="FileCard__thumbnail--empty">No preview</div>
        )}
      </div>

      <div className="FileCard__info">
        {isRenaming ? (
          <input
            type="text"
            className="FileCard__name-input"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={handleKeyDown}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          />
        ) : (
          <h3 className="FileCard__name" title={file.name}>
            {file.name}
          </h3>
        )}
        <p className="FileCard__date">
          {formatRelativeDate(file.lastModified)}
        </p>
      </div>

      <div className="FileCard__actions">
        <FilledButton
          size="medium"
          variant="filled"
          color="primary"
          onClick={() => onSelect(file.id)}
          label="Open"
        >
          Open
        </FilledButton>

        <div className="FileCard__menu">
          <FilledButton
            size="medium"
            variant="icon"
            color="muted"
            onClick={(e) => {
              e.stopPropagation();
              setShowMainMenu(!showMainMenu);
              setShowActionsSubmenu(false);
              setShowMoveSubmenu(false);
            }}
            label="File options"
            aria-label="File options"
            icon={DotsIcon}
          />

          {showMainMenu && (
            <div className="FileCard__dropdown">
              <div
                className="FileCard__dropdown-item FileCard__dropdown-item--parent"
                onMouseEnter={() => {
                  setShowActionsSubmenu(true);
                  setShowMoveSubmenu(false);
                }}
              >
                Actions
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className="FileCard__submenu-arrow"
                >
                  <path
                    d="M4 2L8 6L4 10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {showActionsSubmenu && (
                  <div className="FileCard__submenu">
                    <button
                      className="FileCard__dropdown-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRenameClick();
                      }}
                    >
                      Rename
                    </button>
                  </div>
                )}
              </div>

              {onMoveToFolder && folders.length > 0 && (
                <div
                  className="FileCard__dropdown-item FileCard__dropdown-item--parent"
                  onMouseEnter={() => {
                    setShowMoveSubmenu(true);
                    setShowActionsSubmenu(false);
                  }}
                >
                  Move
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    className="FileCard__submenu-arrow"
                  >
                    <path
                      d="M4 2L8 6L4 10"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {showMoveSubmenu && (
                    <div className="FileCard__submenu">
                      <button
                        className="FileCard__dropdown-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveToFolder(null);
                        }}
                      >
                        Uncategorized
                      </button>
                      {folders.map((folder) => (
                        <button
                          key={folder.id}
                          className="FileCard__dropdown-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveToFolder(folder.id);
                          }}
                        >
                          {folder.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <FilledButton
          size="medium"
          variant={showConfirm ? "filled" : "outlined"}
          color="danger"
          onClick={handleDelete}
          label=""
          aria-label={showConfirm ? "Confirm delete" : "Delete file"}
          icon={showConfirm ? checkIcon : TrashIcon}
        />
      </div>
    </div>
  );
};
